import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    MessageBarBody,
    MessageBarTitle,
    Spinner
} from '@fluentui/react-components';
import { Add20Regular, DocumentArrowDown20Regular } from '@fluentui/react-icons';

import { GuideSectionTitle } from '../../../../theme/components/GuideSectionTitle';
import { GuideCountChip } from '../../../../theme/components/GuideKpiCard';
import { GuideEmptyState } from '../../../../theme/components/GuideEmptyState';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideRowActions, GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName, useGuideSort } from '../../../../theme/components/GuideTable';
import { StatusPill } from '../../../../theme/components/StatusPill';
import { BlockSkeleton, ErrorBanner, HelpButton, UnavailableBanner } from '../PractitionerAccountStatement.Bits';
import { fmtInvoiceAmount, fmtInvoiceDate, InvoiceStatusPill, useInvoicesText } from '../../../CashFlow/Payments/PaymentInvoices/PaymentInvoices.Bits';
import { PaymentInvoicesDrawer, PaymentOption } from '../../../CashFlow/Payments/PaymentInvoices/PaymentInvoices.Drawer';
import { CreditorInvoiceFormDrawer, InvoiceFormMode } from './CreditorInvoice.FormDrawer';
import { SettlementInvoicesDrawer } from './SettlementInvoices.Drawer';
import FilePreview from '../../../Files/FilePreview/FilePreview.Component';

import {
    appliedAmountOf,
    invoiceKpis,
    invoiceLabel,
    invoicePathOf,
    invoiceStatusOf
} from '../../../../functions/CreditorInvoices.Functions';
import { getExtension } from '../../../../functions/AzureFileUpload.Functions';
import { paymentSettlementIds, settlementLabel } from '../PractitionerAccountStatement.Functions';
import {
    CreditorInvoice,
    CreditorInvoiceData,
    CreditorInvoicesEntry,
    CreditorInvoiceSourceEnum,
    PaymentInvoiceAllocation,
    PaymentInvoiceAllocationInput,
    SetPaymentInvoicesResult,
    SettlementInvoiceCandidate
} from '../../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { CreditorPayment } from '../../../../models/Entities/PractitionerAccountStatement/PractitionerAccountStatement';
import { AzureFileUploadResult } from '../../../../models/Entities/AzureFiles/AzureFileUpload';
import { Settlement } from '../../../../models/Entities/Settlements/Settlement';
import { ErrorType } from '../../../../models/HttpError';
import { PreviewFile } from '../../../Files/FilePreview/FilePreview.Functions';
import '../../../CashFlow/Payments/PaymentInvoices/PaymentInvoices.Styles.css';

export interface PractitionerInvoicesActions {
    LoadCreditorInvoices?: (creditorId: string) => unknown;
    UploadCreditorInvoice?: (creditorId: string, file: File, data: CreditorInvoiceData, onProgress?: (percent: number) => void) => Promise<AzureFileUploadResult>;
    RetryRegisterCreditorInvoice?: (creditorId: string, path: string, data: CreditorInvoiceData) => Promise<void>;
    RegisterSettlementInvoice?: (creditorId: string, candidate: SettlementInvoiceCandidate, data: CreditorInvoiceData) => Promise<unknown>;
    GetSettlementInvoiceCandidates?: (creditorId: string, settlementIds: number[]) => Promise<{ candidates: SettlementInvoiceCandidate[]; failedSettlementIds: number[] }>;
    UpdateCreditorInvoice?: (creditorId: string, invoiceId: number, data: CreditorInvoiceData) => Promise<unknown>;
    DeleteCreditorInvoice?: (creditorId: string, invoiceId: number, force?: boolean) => Promise<void>;
    GetPaymentInvoices?: (paymentId: number) => Promise<PaymentInvoiceAllocation[]>;
    SetPaymentInvoices?: (paymentId: number, allocations: PaymentInvoiceAllocationInput[], creditorId?: string) => Promise<SetPaymentInvoicesResult>;
}

export interface PractitionerInvoicesSectionProps extends PractitionerInvoicesActions {
    practitionerId: string;
    practitionerName: string;
    invoices: CreditorInvoicesEntry | undefined;
    settlements: Settlement[];
    currencies: string[];
    payments: CreditorPayment[];
    /** Algo cambió en las facturas de un pago (para refrescar contadores). */
    onPaymentInvoicesChanged?: () => void;
}

const reject = () => Promise.reject({ ErrorCode: 0, ErrorMessage: 'Not available', Errors: [] } as ErrorType);

/**
 * "Facturas del profesional" del estado de cuenta (contrato §12.3): chips por estado, tabla con número,
 * fecha, liquidación, importe, aplicado, estado, pagos que la cubren y origen; acciones por fila
 * (Previsualizar, Editar datos, Asociar a pago, Eliminar) y altas ("+ Subir factura", "Traer facturas de
 * liquidaciones").
 */
export const PractitionerInvoicesSection = (props: PractitionerInvoicesSectionProps) => {
    const tr = useInvoicesText();
    const creditorId = props.practitionerId;
    const entry = props.invoices;
    const invoices = useMemo(() => entry?.data ?? [], [entry?.data]);
    const kpis = useMemo(() => invoiceKpis(invoices), [invoices]);

    const [form, setForm] = useState<{ mode: InvoiceFormMode; invoice?: CreditorInvoice } | undefined>(undefined);
    const [bringOpen, setBringOpen] = useState(false);
    const [associate, setAssociate] = useState<CreditorInvoice | undefined>(undefined);
    const [preview, setPreview] = useState<PreviewFile | undefined>(undefined);
    const [deleting, setDeleting] = useState<CreditorInvoice | undefined>(undefined);
    const [deleteConflict, setDeleteConflict] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState<ErrorType | undefined>(undefined);

    const load = () => props.LoadCreditorInvoices?.(creditorId);

    useEffect(() => {
        if (creditorId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creditorId]);

    const settlementNameOf = (id: number) => settlementLabel(id, props.settlements);

    const sort = useGuideSort<CreditorInvoice>(invoices, {
        number: (invoice) => invoiceLabel(invoice),
        date: (invoice) => (invoice.invoiceDate ? new Date(invoice.invoiceDate).getTime() : undefined),
        settlement: (invoice) => invoice.settlementId ?? undefined,
        amount: (invoice) => invoice.amount ?? undefined,
        applied: (invoice) => appliedAmountOf(invoice),
        status: (invoice) => invoiceStatusOf(invoice)
    });

    const paymentOptions: PaymentOption[] = props.payments.map((payment) => ({
        paymentId: payment.paymentId,
        label: tr('paymentDrawer.paymentOption', {
            id: payment.paymentId,
            date: fmtInvoiceDate(payment.paymentDate),
            amount: fmtInvoiceAmount(payment.amount, payment.currencyCode)
        }),
        settlementIds: paymentSettlementIds(payment)
    }));

    const openPreview = (invoice: CreditorInvoice) => {
        const path = invoicePathOf(invoice);
        if (!path) return;
        setPreview({ path, name: invoiceLabel({ ...invoice, invoiceNumber: null }), extension: invoice.fileExtension || getExtension(invoice.fileName), size: invoice.fileSize });
    };

    const closeDelete = () => {
        setDeleting(undefined);
        setDeleteConflict(false);
        setDeleteError(undefined);
        setDeleteBusy(false);
    };

    const confirmDelete = async (force: boolean) => {
        if (!deleting || !props.DeleteCreditorInvoice) return;
        setDeleteBusy(true);
        setDeleteError(undefined);
        try {
            await props.DeleteCreditorInvoice(creditorId, deleting.id, force);
            closeDelete();
            if (force) props.onPaymentInvoicesChanged?.();
        } catch (error: unknown) {
            setDeleteBusy(false);
            if ((error as ErrorType)?.ErrorCode === 409 && !force) setDeleteConflict(true);
            else setDeleteError(error as ErrorType);
        }
    };

    const renderRow = (invoice: CreditorInvoice) => {
        const name = invoiceLabel(invoice);
        const currency = (invoice.currencyCode || '').toUpperCase();
        const payments = invoice.payments ?? [];
        return (
            <tr key={invoice.id} className={guideRowClassName({ pending: invoiceStatusOf(invoice) === 1 })} data-testid={`pas-invoice-row-${invoice.id}`}>
                <td>
                    <span className="pinv-strong">{invoice.invoiceNumber || tr('noNumber')}</span>
                    <span className="pinv-secondary">{invoiceLabel({ ...invoice, invoiceNumber: null })}</span>
                </td>
                <td className="ichtys-num pas-nowrap">{fmtInvoiceDate(invoice.invoiceDate)}</td>
                <td>{invoice.settlementId ? settlementNameOf(invoice.settlementId) : <span className="pinv-secondary-cell">—</span>}</td>
                <td className={cell('end', 'num')}>{invoice.amount === null || invoice.amount === undefined ? <span className="pinv-secondary-cell">{tr('noAmount')}</span> : fmtInvoiceAmount(invoice.amount, currency)}</td>
                <td className={cell('end', 'num')}>{fmtInvoiceAmount(appliedAmountOf(invoice), currency)}</td>
                <td><InvoiceStatusPill invoice={invoice} /></td>
                <td>
                    {payments.length === 0
                        ? <span className="pinv-secondary-cell">—</span>
                        : payments.map((item) => tr('paymentRef', { id: item.paymentId })).join(', ')}
                </td>
                <td>
                    <StatusPill tone="neutral" symbol={invoice.source === CreditorInvoiceSourceEnum.SettlementFile ? '◇' : '○'}>
                        {invoice.source === CreditorInvoiceSourceEnum.SettlementFile ? tr('source.settlementFile') : tr('source.uploaded')}
                    </StatusPill>
                </td>
                <td className={cell('actions')}>
                    <GuideRowActions
                        label={tr('actions.rowActions', { name })}
                        actions={[
                            { key: 'preview', label: tr('actions.preview'), onClick: () => openPreview(invoice), disabled: !invoicePathOf(invoice) },
                            { key: 'edit', label: tr('actions.edit'), onClick: () => setForm({ mode: 'edit', invoice }) },
                            { key: 'associate', label: tr('actions.associate'), onClick: () => setAssociate(invoice), disabled: props.payments.length === 0 },
                            { key: 'delete', label: tr('actions.delete'), danger: true, onClick: () => setDeleting(invoice) }
                        ]}
                    />
                </td>
            </tr>
        );
    };

    const renderBody = () => {
        if (!entry || entry.status === 'idle' || (entry.status === 'loading' && !entry.data)) return <BlockSkeleton rows={3} testId="pas-invoices-loading" />;
        if (entry.status === 'unavailable') return <UnavailableBanner what={tr('unavailableWhat')} />;
        if (entry.status === 'failed' && invoices.length === 0) return <ErrorBanner title={tr('errorTitle')} error={entry.error} onRetry={load} />;
        if (invoices.length === 0) {
            return (
                <GuideEmptyState
                    title={tr('emptyTitle')}
                    text={tr('emptyText')}
                    data-testid="pas-invoices-empty"
                    action={<Button appearance="primary" icon={<Add20Regular />} onClick={() => setForm({ mode: 'upload' })}>{tr('actions.upload')}</Button>}
                />
            );
        }
        const sortProps = sort.sortProps;
        return (
            <>
                <div className="pinv-counters" data-testid="pas-invoices-kpis">
                    <GuideCountChip label={tr('kpis.unpaid')} count={kpis.unpaid} />
                    <GuideCountChip label={tr('kpis.partial')} count={kpis.partial} />
                    <GuideCountChip label={tr('kpis.paid')} count={kpis.paid} />
                    {kpis.linked > 0 && <GuideCountChip label={tr('kpis.linked')} count={kpis.linked} />}
                </div>
                {entry.status === 'failed' && <ErrorBanner title={tr('errorTitle')} error={entry.error} onRetry={load} />}
                <GuideTable ariaLabel={tr('section.title')} data-testid="pas-invoices-table">
                    <thead>
                        <tr>
                            <GuideTh sort={sortProps('number')}>{tr('columns.number')}</GuideTh>
                            <GuideTh sort={sortProps('date')}>{tr('columns.date')}</GuideTh>
                            <GuideTh sort={sortProps('settlement')}>{tr('columns.settlement')}</GuideTh>
                            <GuideTh align="end" sort={sortProps('amount')}>{tr('columns.amount')}</GuideTh>
                            <GuideTh align="end" sort={sortProps('applied')}>{tr('columns.applied')}</GuideTh>
                            <GuideTh sort={sortProps('status')}>{tr('columns.status')}</GuideTh>
                            <GuideTh>{tr('columns.payments')}</GuideTh>
                            <GuideTh>{tr('columns.source')}</GuideTh>
                            <th className={cell('actions')} scope="col" />
                        </tr>
                    </thead>
                    <tbody>{sort.rows.map(renderRow)}</tbody>
                </GuideTable>
            </>
        );
    };

    const canWrite = entry?.status !== 'unavailable';

    return (
        <section className="pas-block" aria-labelledby="pas-invoices-title" data-testid="pas-invoices">
            <GuideSectionTitle
                id="pas-invoices-title"
                title={tr('section.title')}
                help={<HelpButton title={tr('section.title')} text={tr('section.help')} />}
                description={tr('section.description')}
                actions={canWrite ? (
                    <>
                        <Button appearance="secondary" icon={<DocumentArrowDown20Regular />} onClick={() => setBringOpen(true)}>{tr('actions.fromSettlements')}</Button>
                        <Button appearance="primary" icon={<Add20Regular />} onClick={() => setForm({ mode: 'upload' })}>{tr('actions.upload')}</Button>
                    </>
                ) : undefined}
            />
            {renderBody()}

            <CreditorInvoiceFormDrawer
                open={!!form}
                mode={form?.mode ?? 'upload'}
                invoice={form?.invoice}
                practitionerName={props.practitionerName}
                currencies={props.currencies}
                settlements={props.settlements}
                onDismiss={() => setForm(undefined)}
                onUpload={(file, data, onProgress) => (props.UploadCreditorInvoice ? props.UploadCreditorInvoice(creditorId, file, data, onProgress) : reject())}
                onRetryRegister={(path, data) => (props.RetryRegisterCreditorInvoice ? props.RetryRegisterCreditorInvoice(creditorId, path, data) : reject())}
                onUpdate={(invoiceId, data) => (props.UpdateCreditorInvoice ? props.UpdateCreditorInvoice(creditorId, invoiceId, data) : reject())}
                onRegisterSettlement={(candidate, data) => (props.RegisterSettlementInvoice ? props.RegisterSettlementInvoice(creditorId, candidate, data) : reject())}
                onDone={() => setForm(undefined)}
            />

            {bringOpen && (
                <SettlementInvoicesDrawer
                    open={bringOpen}
                    onDismiss={() => setBringOpen(false)}
                    practitionerName={props.practitionerName}
                    settlements={props.settlements}
                    currencies={props.currencies}
                    GetCandidates={(ids) => (props.GetSettlementInvoiceCandidates ? props.GetSettlementInvoiceCandidates(creditorId, ids) : reject())}
                    onRegister={(candidate, data) => (props.RegisterSettlementInvoice ? props.RegisterSettlementInvoice(creditorId, candidate, data) : reject())}
                />
            )}

            {associate && (
                <PaymentInvoicesDrawer
                    open={!!associate}
                    onDismiss={() => setAssociate(undefined)}
                    paymentOptions={paymentOptions}
                    preselectInvoiceId={associate.id}
                    creditorId={creditorId}
                    creditorName={props.practitionerName}
                    invoices={entry}
                    settlementNameOf={settlementNameOf}
                    LoadCreditorInvoices={() => undefined}
                    GetPaymentInvoices={(id) => (props.GetPaymentInvoices ? props.GetPaymentInvoices(id) : reject())}
                    SetPaymentInvoices={(id, allocations) => (props.SetPaymentInvoices ? props.SetPaymentInvoices(id, allocations, creditorId) : reject())}
                    onSaved={() => props.onPaymentInvoicesChanged?.()}
                />
            )}

            {preview && <FilePreview open={!!preview} file={preview} onDismiss={() => setPreview(undefined)} />}

            <Dialog open={!!deleting} modalType="alert" onOpenChange={(_event, data) => { if (!data.open && !deleteBusy) closeDelete(); }}>
                <DialogSurface data-testid="pas-invoice-delete-dialog">
                    <DialogBody>
                        <DialogTitle>{deleteConflict ? tr('delete.conflictTitle') : tr('delete.title')}</DialogTitle>
                        <DialogContent>
                            {deleteConflict
                                ? (
                                    <GuideMessageBar intent="warning" layout="multiline" data-testid="pas-invoice-delete-conflict">
                                        <MessageBarBody>{tr('delete.conflictText')}</MessageBarBody>
                                    </GuideMessageBar>
                                )
                                : <p>{tr('delete.text', { name: deleting ? invoiceLabel(deleting) : '' })}</p>}
                            {deleteError && (
                                <GuideMessageBar intent="error" layout="multiline">
                                    <MessageBarBody>
                                        <MessageBarTitle>{tr('delete.error')}</MessageBarTitle>
                                        {deleteError.ErrorMessage}
                                    </MessageBarBody>
                                </GuideMessageBar>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" disabled={deleteBusy} onClick={closeDelete}>{tr('delete.cancel')}</Button>
                            <Button
                                appearance="secondary"
                                className="pinv-danger"
                                disabled={deleteBusy}
                                icon={deleteBusy ? <Spinner size="extra-tiny" /> : undefined}
                                onClick={() => confirmDelete(deleteConflict)}
                            >
                                {deleteConflict ? tr('delete.force') : tr('delete.confirm')}
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </section>
    );
};

export default PractitionerInvoicesSection;
